import React from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router';
import { Header } from '@app/navigation/header';
import { Body, Container, Body5, H5, Page } from '@ui';
import { BackArrowButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS } from '@constants';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { FetchSpecForm } from '@features/scoreSpecFetch/FetchSpecForm';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const FETCH_SCORE_SPEC_HEADING_ID = 'fetch-score-spec-heading';

// The read counterpart to /create-score-spec. Same grant, one control.
//
// Mounted twice: bare, where the picker is the whole interaction, and as `/get-score-spec/:specId`,
// where the specs list sends a clicked row. The second form is why this screen is now the landing
// place for "what IS this spec" — the row knows the id, so asking for it again would be theatre.
export const FetchScoreSpec = () => {
  const navigate = useNavigate();
  // Absent on the bare route; the form then waits for the picker instead of fetching on arrival.
  const { specId } = useParams();
  const tools = useCurrentUserToolsAvailable();

  React.useEffect(() => {
    if (tools && tools.length > 0 && !tools.includes(AI_TOOLS.SCORE_SPEC_ADMIN)) {
      navigate(ROUTES.home);
    }
  }, [tools, navigate]);

  React.useEffect(() => {
    document.getElementById(FETCH_SCORE_SPEC_HEADING_ID)?.focus();
  }, []);

  // Back to the specs list, not home. This screen is reached from that list — by a row click or by
  // its "Fetch a spec by id" button — so the list is where back means something.
  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width="95%">
          <BackArrowButton onClick={() => navigate(ROUTES.scoreSpec)} aria-label="Back to specs" />
          <Container top={48}>
            <H5 id={FETCH_SCORE_SPEC_HEADING_ID} tabIndex={-1}>
              Fetch a Score Spec
            </H5>
            <Container top={8}>
              <Body5 isLowContrast>
                Pick a spec, get its record. Formatted, but not filtered — including the operational columns the
                definition itself never carries.
              </Body5>
            </Container>
            <Container top={24}>
              <FetchSpecForm initialId={specId} />
            </Container>
          </Container>
        </Page>
      </Body>
    </PageWrapper>
  );
};

export default FetchScoreSpec;
